export enum MerchantState {
  INITIAL = 'initial',
  SUBMITTING = 'submitting',
  SUBMITTED = 'submitted',

  // TFA State
  TFA_ENABLED = 'tfaEnabled',

  // KYC States
  KYC_CREATED = 'kycCreated',
  KYC_ID_CHECK_COMPLETED = 'kycIdCheckCompleted',
  PENDING_ID_DATA_BANK_DATA_MATCH_CHECK = 'pendingIdDataBankDataMatchCheck',
  KYC_ID_BANK_DATA_MATCHED = 'kycIdBankDataMatched',
  KYC_ID_BANK_DATA_MISSMATCH = 'kycIdBankDataMissmatch',
  KYC_PENDING_PEP_CHECK = 'kycPendingPepCheck',
  KYC_FAILED = 'kycFailed',
  KYC_EXPIRED = 'kycExpired',
  KYC_APPROVED = 'kycApproved',
  KYC_DECLINED = 'kycDeclined',
  PAUSED = 'paused',
  ACTIVE = 'active',

  ERROR = 'error'
}

export enum MerchantEvent {
  SUBMIT_DETAILS = 'submitDetails',
  SUBMISSION_SUCCESS = 'submissionSuccess',
  SUBMISSION_ERROR = 'submissionError',

  // TFA Events
  ENABLE_TFA = 'enableTfa',

  // KYC Events
  KYC_CREATE = 'kycCreate',
  KYC_START = 'kycStart',
  KYC_ID_CHECK_COMPLETE = 'kycIdCheckComplete',
  START_ID_BANK_DATA_CHECK = 'startIdBankDataCheck',
  ID_BANK_DATA_MATCH = 'idBankDataMatch',
  ID_BANK_DATA_MISMATCH = 'idBankDataMismatch',
  KYC_START_PEP_CHECK = 'kycStartPepCheck',
  KYC_FAIL = 'kycFail',
  KYC_EXPIRE = 'kycExpire',
  KYC_APPROVE = 'kycApprove',
  KYC_DECLINE = 'kycDecline',
  PAUSE = 'pause'
}

export class MerchantStateMachine {
  private state: MerchantState;
  private listeners: ((state: MerchantState) => void)[] = [];

  constructor(initialState: MerchantState = MerchantState.INITIAL) {
    this.state = initialState;
  }

  getState(): MerchantState {
    return this.state;
  }

  setStateDebug(state: MerchantState) {
    this.state = state;

  }


  onStateChange(callback: (state: MerchantState) => void) {
    this.listeners.push(callback);
  }

  transition(event: MerchantEvent): void {
    const currentTransitions = this.transitions[this.state];
    if (currentTransitions && currentTransitions[event]) {
      this.state = currentTransitions[event];
      this.listeners.forEach(listener => listener(this.state));
    } else {
      console.warn(`Invalid transition: ${this.state} -> ${event}`);
    }
  }

  private transitions = {
    [MerchantState.INITIAL]: {
      [MerchantEvent.SUBMIT_DETAILS]: MerchantState.SUBMITTING,
      [MerchantEvent.PAUSE]: MerchantState.PAUSED
    },
    [MerchantState.SUBMITTING]: {
      [MerchantEvent.SUBMISSION_SUCCESS]: MerchantState.SUBMITTED,
      [MerchantEvent.SUBMISSION_ERROR]: MerchantState.ERROR
    },
    [MerchantState.SUBMITTED]: {
      [MerchantEvent.ENABLE_TFA]: MerchantState.TFA_ENABLED,
      [MerchantEvent.PAUSE]: MerchantState.PAUSED
    },
    [MerchantState.TFA_ENABLED]: {
      [MerchantEvent.KYC_CREATE]: MerchantState.KYC_CREATED,
      [MerchantEvent.PAUSE]: MerchantState.PAUSED
    },
    [MerchantState.KYC_CREATED]: {
      [MerchantEvent.KYC_ID_CHECK_COMPLETE]: MerchantState.KYC_ID_CHECK_COMPLETED,
      [MerchantEvent.PAUSE]: MerchantState.PAUSED
    },
    [MerchantState.KYC_ID_CHECK_COMPLETED]: {
      [MerchantEvent.START_ID_BANK_DATA_CHECK]: MerchantState.PENDING_ID_DATA_BANK_DATA_MATCH_CHECK,
      [MerchantEvent.KYC_FAIL]: MerchantState.KYC_FAILED,
      [MerchantEvent.KYC_EXPIRE]: MerchantState.KYC_EXPIRED,
      [MerchantEvent.PAUSE]: MerchantState.PAUSED
    },
    [MerchantState.PENDING_ID_DATA_BANK_DATA_MATCH_CHECK]: {
      [MerchantEvent.ID_BANK_DATA_MATCH]: MerchantState.KYC_ID_BANK_DATA_MATCHED,
      [MerchantEvent.ID_BANK_DATA_MISMATCH]: MerchantState.KYC_ID_BANK_DATA_MISSMATCH,
      [MerchantEvent.PAUSE]: MerchantState.PAUSED
    },
    [MerchantState.KYC_ID_BANK_DATA_MATCHED]: {
      [MerchantEvent.KYC_START_PEP_CHECK]: MerchantState.KYC_PENDING_PEP_CHECK,
      [MerchantEvent.PAUSE]: MerchantState.PAUSED
    },
    [MerchantState.KYC_ID_BANK_DATA_MISSMATCH]: {
      [MerchantEvent.KYC_CREATE]: MerchantState.KYC_CREATED,
      [MerchantEvent.PAUSE]: MerchantState.PAUSED
    },
    [MerchantState.KYC_PENDING_PEP_CHECK]: {
      [MerchantEvent.KYC_APPROVE]: MerchantState.KYC_APPROVED,
      [MerchantEvent.KYC_DECLINE]: MerchantState.KYC_DECLINED,
      [MerchantEvent.PAUSE]: MerchantState.PAUSED
    },
    [MerchantState.KYC_FAILED]: {
      [MerchantEvent.KYC_CREATE]: MerchantState.KYC_CREATED
    },
    [MerchantState.KYC_EXPIRED]: {
      [MerchantEvent.KYC_CREATE]: MerchantState.KYC_CREATED,
      [MerchantEvent.PAUSE]: MerchantState.PAUSED
    },
    [MerchantState.PAUSED]: {
      [MerchantEvent.KYC_APPROVE]: MerchantState.KYC_APPROVED,
      [MerchantEvent.KYC_DECLINE]: MerchantState.KYC_DECLINED
    }
  };
}

// Helper function to get display message based on state
export function getStateMessage(state: MerchantState): string {
  switch (state) {
    case MerchantState.INITIAL:
      return 'Ready to set up payment details';
    case MerchantState.SUBMITTING:
      return 'Submitting payment details...';
    case MerchantState.SUBMITTED:
      return 'Payment details submitted';
    case MerchantState.TFA_ENABLED:
      return 'Two-factor authentication enabled';
    case MerchantState.KYC_CREATED:
      return 'KYC verification created';
    case MerchantState.KYC_ID_CHECK_COMPLETED:
      return 'ID verification completed, additional checks in progress';
    case MerchantState.KYC_PENDING_PEP_CHECK:
      return 'Final verification checks in progress';
    case MerchantState.KYC_FAILED:
      return 'KYC verification failed';
    case MerchantState.KYC_EXPIRED:
      return 'KYC verification expired';
    case MerchantState.PAUSED:
      return 'Account is currently paused';
    case MerchantState.ERROR:
      return 'An error occurred';
    case MerchantState.PENDING_ID_DATA_BANK_DATA_MATCH_CHECK:
      return 'Verifying ID matches bank account details';
    case MerchantState.KYC_ID_BANK_DATA_MATCHED:
      return 'ID and bank details verified';
    case MerchantState.KYC_ID_BANK_DATA_MISSMATCH:
      return 'ID and bank details do not match';
    default:
      return 'Unknown state';
  }
} 


// WEBPACK FOOTER //
// ./src/app/shared/machines/merchant.state.machine.ts